import React, { memo } from 'react';
import PropTypes from 'prop-types';

import { Typography } from '@mui';

import styles from './unstyled-heading.scss';
import classNames from 'classnames';

const UnstyledHeading = ({
  ariaOnly = false,
  children,
  className,
  id,
  rank = 1,
  routeFocusSkip = false,
  variant,
  ...passThroughProps
}) => {
  const contentClassName = classNames(styles.content, className, {[styles.visuallyHidden]: ariaOnly});
  const content = (variant ?
    <Typography {...passThroughProps} variant={variant} classes={{root: contentClassName}}>{children}</Typography> :
    <span {...passThroughProps} className={contentClassName}>{children}</span>
  );
  const headerProps = {};
  if (id) {
    headerProps.id = id;
  }
  if (routeFocusSkip) {
    // Marks a landmark/section heading (e.g. "Map Tray") that exists purely for jump-by-
    // heading navigation, not to identify the page itself - AuthenticatedLayout's route-change
    // focus/title logic skips these and looks for the next heading instead.
    headerProps['data-route-focus-skip'] = 'true';
  }
  switch (rank) {
  case 1:
    return (
      <h1 {...headerProps} className={styles.headingInherit}>
        {content}
      </h1>
    );
  case 2:
    return (
      <h2 {...headerProps} className={styles.headingInherit}>
        {content}
      </h2>
    );
  case 3:
    return (
      <h3 {...headerProps} className={styles.headingInherit}>
        {content}
      </h3>
    );
  case 4:
    return (
      <h4 {...headerProps} className={styles.headingInherit}>
        {content}
      </h4>
    );
  case 5:
    return (
      <h5 {...headerProps} className={styles.headingInherit}>
        {content}
      </h5>
    );
  case 6:
    return (
      <h6 {...headerProps} className={styles.headingInherit}>
        {content}
      </h6>
    );
  default:
    return (
      <span role="heading" aria-level={rank} {...headerProps} className={styles.headingInherit}>
        {content}
      </span>
    );
  }
};

UnstyledHeading.propTypes = {
  ariaOnly: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  rank: PropTypes.number,
  routeFocusSkip: PropTypes.bool,
  variant: PropTypes.string
};

export default memo(UnstyledHeading);
